/*
 * Cookie consent for vfr.topmeteo.eu.
 *
 * Webpack entry "consent" (listed in `uncommon`, like gtm), loaded in
 * base.html BEFORE gtm.js. Exposes window.TopMeteoConsent:
 *
 *   get()                  -> {s, m} or null while the visitor has not chosen
 *   onGranted(purpose, fn) -> runs fn now if granted, else once it is granted
 *   open()                 -> shows the settings layer (footer link)
 *
 * Purposes: 's' statistics (GTM / GA4), 'm' marketing (tm_campaign).
 * The server only reads the cookie (topmeteo/utils/consent.py).
 */

import Cookies from 'js-cookie';

import {MarketingKey, StatisticKey, TimestampKey, VersionKey} from './keys';

const banner = document.getElementById('tm-consent');

const CookieName = 'tm_consent';
const Version = banner ? Number(banner.dataset.version) : null;
const LifetimeDays = 365;
const CampaignCookieDomain = 'topmeteo.eu';

const waiting = {
    [StatisticKey]: [],
    [MarketingKey]: [],
};

const createChoice = (statistic, marketing) => ({
    [StatisticKey]: statistic,
    [MarketingKey]: marketing,
});

const read = () => {
    const data = Cookies.getJSON(CookieName);

    if (!data || data[VersionKey] !== Version) return null;

    return createChoice(data[StatisticKey] === true, data[MarketingKey] === true);
};

const hasCampaignInURL = () => 'campaignInUrl' in banner.dataset;

const removeAnalyticsCookies = () => {
    Object.keys(Cookies.get()).forEach(name => {
        if (name === '_ga' || name.indexOf('_ga_') === 0 || name === '_gid' || name === '_gat') {
            Cookies.remove(name, {path: '/'});
            Cookies.remove(name, {path: '/', domain: `.${CampaignCookieDomain}`});
        }
    });
};

const removeCampaignCookie = () => {
    Cookies.remove('tm_campaign', {path: '/', domain: CampaignCookieDomain});
    Cookies.remove('tm_campaign', {path: '/'});
};

const run = choice => {
    [StatisticKey, MarketingKey].forEach(purpose => {
        if (!choice[purpose]) return;

        waiting[purpose].splice(0).forEach(fn => fn());
    });
};

const save = choice => {
    const previous = read();

    Cookies.set(CookieName, {
        [VersionKey]: Version,
        [StatisticKey]: choice[StatisticKey],
        [MarketingKey]: choice[MarketingKey],
        [TimestampKey]: new Date().toISOString().slice(0, 10),
    }, {
        expires: LifetimeDays,
        path: '/',
        sameSite: 'Lax',
        secure: window.location.protocol === 'https:',
    });

    let reload = false;

    if (previous && previous[StatisticKey] && !choice[StatisticKey]) {
        removeAnalyticsCookies();
        /* GTM is already running in this page */
        reload = true;
    }

    if (previous && previous[MarketingKey] && !choice[MarketingKey]) {
        removeCampaignCookie();
    }

    if (choice[MarketingKey] && hasCampaignInURL()) {
        /* the middleware stores the campaign now and strips the URL */
        reload = true;
    }

    if (reload) {
        window.location.reload();

        return;
    }

    run(choice);
};

/* UI */

const showSettings = show => {
    if (!banner) return;

    const settings = banner.querySelector('#tm-consent-settings');
    const first = banner.querySelector('[data-consent-layer="first"]');
    const toggle = banner.querySelector('[data-consent-action="settings"]');
    const current = read() || createChoice(false, false);

    banner.querySelectorAll('[data-consent-purpose]').forEach(input => {
        input.checked = current[input.dataset.consentPurpose] === true;
    });

    settings.hidden = !show;
    first.hidden = show;
    toggle.setAttribute('aria-expanded', String(show));

    if (show) settings.querySelector('input:not([disabled])').focus();
};

const close = () => {
    banner.hidden = true;
};

const open = () => {
    if (!banner) return;

    banner.hidden = false;
    showSettings(true);
};

const selection = () => {
    const choice = createChoice(false, false);

    banner.querySelectorAll('[data-consent-purpose]').forEach(input => {
        choice[input.dataset.consentPurpose] = input.checked;
    });

    return choice;
};

const actions = {
    'accept-all': () => save(createChoice(true, true)),
    'necessary-only': () => save(createChoice(false, false)),
    save: () => save(selection()),
    settings: () => showSettings(true),
};

if (banner) {
    banner.addEventListener('click', event => {
        const button = event.target.closest('[data-consent-action]');

        if (!button) return;

        const action = button.dataset.consentAction;

        actions[action]();

        if (action !== 'settings') close();
    });

    if (read() === null) {
        banner.hidden = false;
    }
}

document.querySelectorAll('.tm-consent-open').forEach(link => {
    link.addEventListener('click', event => {
        event.preventDefault();
        open();
    });
});

window.TopMeteoConsent = {
    get: read,
    onGranted: (purpose, fn) => {
        const current = read();

        if (current && current[purpose]) {
            fn();
        } else {
            waiting[purpose].push(fn);
        }
    },
    open,
};
